import React, { useState, useRef, useEffect } from 'react';
import { RouteComponentProps } from 'react-router';
import ConversationsErrorBoundary from './ErrorBoundary';
import ConversationThread, { SubmitPayload } from './Thread';
import ConversationList from './List';
import Loading from 'components/CenteredLoadingIndicator';
import ToastActions from 'actions/ToastActions';
import MetricTracking from 'lib/MetricTracking';
import invariant from 'tiny-invariant';
import CurrentUser from 'auth/CurrentUser';
import _ from 'lodash';
import { useApolloClient } from '@apollo/client';
import BlockedContentDialog from 'chat/components/_shared/BlockedContentDialog/BlockedContentDialog';
import ORGANIZATION_HOME_SCREEN from 'components/OrgHeader/OrganizationHomeScreen.gql';
import AlertDialog from 'components/AlertDialogV2';
import { uuidv7 as uuid } from 'lib/uuidv7';

import Notice from 'components/Notice';
import { getBasePath, getConvoPath } from './getPath';
import * as model from './model';

import {
  CompositionUserRecipientInput,
  CompositionConversationRecipientInput,
} from 'globalTypes.hq';

import {
  optimistic,
  useOrg,
  useConvoList,
  useConvoListUnreadsSubscription,
  useConvoMessages,
  useUserProfile,
  useNewOneOnOneConversation,
  useSendConversationMessage,
  useClearUnreads,
} from './gql';
import type { ConversationMessagesHQ, ConversationMessagesHQVariables } from './gql';
import type { Conversation } from './gql';

import './index.scss';

interface Props
  extends RouteComponentProps<
    { groupCode: string },
    {
      convoUuid?: string;
      /** This is used for new chats, if it's present then it's a new chat */
      newConvoUserUuid?: string;
    },
    Props
  > {}

export default function ConversationsRouteHandler(props: Props) {
  const { convoUuid, newConvoUserUuid } = props.routeParams;
  const groupCode = props.params.groupCode;

  const [blockedContentError, setBlockedContentError] = useState<string | undefined>();
  const [sendError, setSendError] = useState<string>('');
  // CAREFUL the underlying search coponennt is not fully controlled
  // and so the initial value that we set here is not respected
  const [searchQuery, setSearchQuery] = useState('');

  const org = useOrg({ variables: { code: `@${groupCode}` }, fetchPolicy: 'cache-first' });
  const klass = org.data?.classes?.[0] ?? undefined;
  const orgUuid = model.Group.getOrgUuid(klass) ?? '';

  // TODO use searchQuery
  const convos = useConvoList({
    skip: !orgUuid,
    variables: {
      input: { organization: orgUuid ?? '' },
    },
  });
  const currentConvo = convos.data?.conversationsList.items.find(
    (convo) => convo.conversationKey.conversationUuid === convoUuid
  );

  const convoMessages = useConvoMessages({
    skip: !convoUuid,
    variables: {
      input: {
        conversationKey: {
          organizationUuid: orgUuid ?? '',
          conversationUuid: convoUuid ?? '',
        },
      },
    },
  });

  useRealTimeUpdates({
    orgUuid,
    convoUuid,
    refetchConvo: () => convos.fetchMore({}),
    refetchMessages: () => convoMessages.fetchMore({}),
  });

  const newConvoUser = useUserProfile({
    skip: !newConvoUserUuid,
    variables: { input: { relatedUserUuid: newConvoUserUuid ?? '' } },
  });
  const [sendNewConvo] = useNewOneOnOneConversation({ refetchQueries: [ORGANIZATION_HOME_SCREEN] });
  const sendToExistingConvo = useSendToExistingConvo();

  useMarkConvoAsRead({ convoUuid, convo: currentConvo, orgUuid });

  const isNewConvo = !!newConvoUserUuid && !convoUuid;

  async function onSend(
    payload: SubmitPayload,
    { scrollToBottom }: { scrollToBottom: () => void }
  ) {
    try {
      if (isNewConvo) {
        invariant(newConvoUserUuid);
        const optimisticConversationUuid = uuid();
        invariant(optimisticConversationUuid, 'bad optimisticConversationUuid');
        const optimisticMessageUuid = uuid();
        invariant(optimisticMessageUuid, 'bad optimisticMessageUuid');

        await sendNewConvo({
          variables: {
            input: {
              userRecipient: { userUuid: newConvoUserUuid, orgUuid },
              optimisticConversationUuid,
              optimisticMessageUuid,
              message: {
                body: payload.text,
                languageCode: payload.detectedTextLanguage,
                translations: payload.translations,
              },
            },
          },
        });

        // refetch the convo list but don't wait for it since we still have work to do
        convos.refetch();

        props.router.push({
          pathname: getConvoPath(groupCode, optimisticConversationUuid),
        });
      } else {
        invariant(convoUuid);

        const lastSequenceId =
          convoMessages.data?.conversationMessages.messages[0]?.sequenceId ?? 0;

        const res = await sendToExistingConvo({
          convoUuid,
          orgUuid,
          lastSequenceId,
          payload,
          scrollToBottom,
        });

        const failure = res.data?.sendConversationMessage.failure;
        if (failure) {
          switch (failure.type) {
            case 'contentModerated': {
              setBlockedContentError(failure.reason);
              return;
            }
            default: {
              setSendError(failure.reason);
              return;
            }
          }
        }

        // TODO until we get real time updates for actual messages (as opposed to just unreads)
        // we need to refetch the list of messages, mostly to update the "sending" state into "sent"
        await convoMessages.fetchMore({});
      }

      MetricTracking.increment('conversations.send.success');
    } catch (err: any) {
      MetricTracking.increment('conversations.send.error');
      console.error('Conversation#send failed', err);
      ToastActions.showErrorToast(`Error sending message`);
    }
  }

  const isFirstTimeLoading =
    org.loading ||
    // this should almost never happen given the new convo flow
    newConvoUser.loading ||
    // convos are loading for the first time i.e. there's no previous data
    (!convos.data?.conversationsList.items.length && convos.loading);

  if (isFirstTimeLoading) {
    return <Loading />;
  }

  // Check if there's a conversation selected otherwise redirect to first
  if (!convoUuid && !newConvoUserUuid) {
    if (convos.data?.conversationsList.items.length) {
      const firstConvo = convos.data.conversationsList.items[0];
      props.router.push({
        pathname: getConvoPath(groupCode, firstConvo.conversationKey.conversationUuid),
      });
      return null;
    } else {
      return (
        <div className="reporting-container">
          <Notice color="whiteout">Start a new conversation from People list</Notice>
        </div>
      );
    }
  }

  if (org.error || newConvoUser.error || convos.error || convoMessages.error) {
    console.error(
      'Error loading org data',
      org.error || newConvoUser.error || convos.error || convoMessages.error
    );
    return (
      <div className="reporting-container">
        <Notice color="whiteout">Sorry, there was an error. Try refreshing the page.</Notice>
      </div>
    );
  }

  const messageRecipient = isNewConvo
    ? { userRecipient: { userUuid: newConvoUserUuid, orgUuid } as CompositionUserRecipientInput }
    : {
        conversationRecipient: {
          conversationKey: {
            organizationUuid: orgUuid,
            conversationUuid: convoUuid,
          },
        } as CompositionConversationRecipientInput,
      };
  return (
    <React.StrictMode>
      <ConversationsErrorBoundary>
        {blockedContentError && (
          <BlockedContentDialog
            onRequestHide={() => setBlockedContentError(undefined)}
            errorMessage={blockedContentError}
          />
        )}
        <AlertDialog
          open={!!sendError}
          onClose={() => setSendError('')}
          title="Message not sent"
          subtext={sendError}
        />
        <div className="Conversations">
          <ConversationList
            className="Conversations-sidebar"
            convos={convos.data}
            onQuery={(query) => setSearchQuery(query)}
            basePath={getBasePath(groupCode)}
            loading={convos.loading}
            newConvoUser={newConvoUser.data?.userProfileScreen.identification}
            onNewConvoClose={() => {
              const firstConvo = convos.data?.conversationsList.items[0];
              props.router.push({
                pathname: getConvoPath(groupCode, firstConvo?.conversationKey.conversationUuid),
              });
            }}
            onLoadMore={() => {
              convos.fetchMore({
                variables: {
                  input: {
                    organization: orgUuid ?? '',
                    nextPageTimestamp: convos.data?.conversationsList.nextPageTimestamp,
                  },
                },
              });
            }}
          />
          <div className="Conversations-content">
            <div className="Conversations-content-inner">
              <ConversationThread
                orgUuid={orgUuid}
                recipient={messageRecipient}
                isClass={model.Group.isClass(klass)}
                convo={currentConvo}
                convoMessages={convoMessages.data?.conversationMessages}
                onSend={onSend}
                loading={convoMessages.loading}
                onLoadMore={() => {
                  invariant(convoUuid, 'onLoadMore convoUuid cannot be undefined');
                  if (!convoMessages.data?.conversationMessages.hasMoreSlices) {
                    return;
                  }
                  const messages = convoMessages.data.conversationMessages.messages;
                  const lastMessage = messages[messages.length - 1];
                  // We need to manually pass type parameters otherwise this does not type check 100%,
                  // due to inference, even though fetchMore already comes typed
                  convoMessages.fetchMore<ConversationMessagesHQ, ConversationMessagesHQVariables>({
                    variables: {
                      input: {
                        conversationKey: {
                          organizationUuid: orgUuid,
                          conversationUuid: convoUuid,
                        },
                        sliceBeforeSeq: lastMessage.sequenceId,
                      },
                    },
                  });
                }}
              />
            </div>
          </div>
        </div>
      </ConversationsErrorBoundary>
    </React.StrictMode>
  );
}

export function useRealTimeUpdates({
  orgUuid,
  convoUuid,
  refetchConvo,
  refetchMessages,
}: {
  orgUuid?: string;
  convoUuid?: string;
  refetchConvo?: () => void;
  refetchMessages?: () => void;
}) {
  const sub = useConvoListUnreadsSubscription({
    skip: !orgUuid,
    context: { hq: true },
    variables: { orgUuid: orgUuid ?? '' },
    shouldResubscribe: true,
    onSubscriptionData: ({ subscriptionData }) => {
      const payload = subscriptionData.data?.conversationListUnreads;
      if (!payload) {
        return;
      }

      refetchConvo?.();
      // if the current conversation got an update then refetch it
      if (payload.conversationKey.conversationUuid === convoUuid) {
        refetchMessages?.();
      }
    },
  });

  const err = sub.error;
  useEffect(() => {
    if (err) {
      ToastActions.showErrorToast(`Error subscribing to real time updates`);
      console.error('Subscribing to conversations real time updates failed', err);
    }
  }, [err]);
}

/**
 *  Mark a conversation as read when selected
 */
export function useMarkConvoAsRead({
  convoUuid,
  convo,
  orgUuid,
}: {
  convoUuid?: string;
  convo?: Conversation;
  orgUuid?: string;
}) {
  const client = useApolloClient();
  const [clearUnreads] = useClearUnreads();

  // debounced version to avoid calling clear multplie times
  const clearUnreadsRef = useRef(_.throttle(clearUnreads, 2000));
  useEffect(() => {
    clearUnreadsRef.current = _.throttle(clearUnreads, 2000);
  }, [clearUnreads]);

  const isUnread = convo?.isUnread;
  useEffect(() => {
    async function effect() {
      // if there's a conversation selected and the conversation is unread then
      // mark it as read
      if (orgUuid && convoUuid && isUnread) {
        try {
          await clearUnreadsRef.current({
            variables: {
              input: {
                conversationKey: { organizationUuid: orgUuid, conversationUuid: convoUuid },
              },
            },
          });
          optimistic.markAsRead({ client, convoUuid, orgUuid: orgUuid });
        } catch (err) {
          console.error("Couldn't mark conversation as read", err);
        }
      }
    }

    effect();
  }, [client, convoUuid, isUnread, orgUuid]);
}

export function useSendToExistingConvo() {
  const client = useApolloClient();
  const [sendConvo] = useSendConversationMessage();

  return async ({
    convoUuid,
    orgUuid,
    payload,
    lastSequenceId,
    scrollToBottom,
  }: {
    convoUuid: string;
    orgUuid: string;
    payload: SubmitPayload;
    lastSequenceId: number;
    scrollToBottom: () => void;
  }) => {
    const sequenceId = lastSequenceId + 1;
    const messageUuid = uuid();
    invariant(messageUuid, 'bad messageUuid');
    const compositeKey = optimistic.getMessageCompositeKey({ convoUuid, messageUuid });

    optimistic.insertMessage({
      client,
      orgUuid,
      convoUuid,
      compositeKey,
      currentUserUuid: CurrentUser.getUserUuid(),
      sequenceId,
      text: payload.text,
    });

    // differ scrollingToTheBottom a bit
    setTimeout(() => {
      scrollToBottom();
    });

    try {
      const res = await sendConvo({
        client,
        variables: {
          input: {
            conversationRecipient: {
              conversationKey: {
                organizationUuid: orgUuid,
                conversationUuid: convoUuid,
              },
            },
            optimisticMessageUuid: messageUuid,
            message: {
              body: payload.text,
              languageCode: payload.detectedTextLanguage,
              translations: payload.translations,
            },
          },
        },
      });

      // note that there are two possible error modes,
      // in here we handle the case where our backend returns an error,
      // but we wrap this whole thing in a try catch to handle the case where
      // the request errors harder
      if (res.data?.sendConversationMessage.failure) {
        optimistic.removeMessage({
          client,
          orgUuid,
          convoUuid,
          sequenceId,
        });
      }

      return res;
    } catch (err) {
      optimistic.removeMessage({
        client,
        orgUuid,
        convoUuid,
        sequenceId,
      });
      throw err;
    }
  };
}

// Used to determine wheather we should display the convo tab or not
// TODO remove when the old GroupNavigationTabs is removed
export function useShouldDisplayConversationsTab(orgUuid?: string) {
  const convos = useConvoList({
    skip: !orgUuid,
    variables: {
      input: { organization: orgUuid ?? '' },
    },
  });

  const sub = useConvoListUnreadsSubscription({
    skip: !orgUuid,
    context: { hq: true },
    variables: { orgUuid: orgUuid ?? '' },
    shouldResubscribe: true,
    onSubscriptionData: ({ subscriptionData }) => {
      const payload = subscriptionData.data?.conversationListUnreads;
      if (!payload) {
        return;
      }

      convos.fetchMore({});
    },
  });

  // silently fail since this is like an extra level of niceness that we are adding
  const err = sub.error;
  if (err) {
    console.warn('Subscribing to conversations real time updates failed', err);
  }

  const hasConvos = !!convos.data?.conversationsList.items.length;
  return hasConvos;
}
